/**
 * useLoungeReactions - Ephemeral Lounge Reactions System
 * 
 * Purpose: Live reaction bubbles that rise and fade (Zoom/Clubhouse style)
 * Integrates with Phase 1 useLoungeSession for automatic cleanup
 * 
 * Phase 2 - Make the Lounge feel alive
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

/**
 * Supported reaction types - includes spicy emojis for Unity Love
 */
export const REACTION_TYPES = {
  // Core reactions
  heart: "❤️",
  heartEyes: "😍",
  fire: "🔥",
  laugh: "😂",
  // Spicy reactions 🌶️
  nervous: "😅",
  mindBlown: "🤯",
  hot: "🥵",
  splash: "💦",
  eggplant: "🍆",
  devil: "😈",
  // Additional
  kiss: "😘",
  party: "🥳",
  shock: "😱",
  cry: "😢",
  clap: "👏",
  thumbsUp: "👍",
  thumbsDown: "👎",
  wave: "👋",
  star: "✨",
  "100": "💯",
};

/**
 * Active reaction in the UI
 * @typedef {Object} ActiveReaction
 * @property {string} id - Unique identifier for the reaction
 * @property {string} type - Reaction type/emoji
 * @property {number} createdAt - Timestamp when reaction was created
 * @property {number} xPercent - Horizontal position (0-100) for spreading bubbles
 */

/**
 * Hook for managing lounge-wide reactions
 * 
 * Features:
 * - Listens to reactions channel via Ably
 * - Auto-expires reactions after 1.8s
 * - Throttles users to max 3 reactions per 2s
 * - Registers cleanup with useLoungeSession
 */
export function useLoungeReactions({
  loungeId,
  userId,
  ably,
  registerCleanup,
}) {
  const [activeReactions, setActiveReactions] = useState([]);
  const channelRef = useRef(null);
  const recentTimestampsRef = useRef([]);

  // Constants
  const REACTION_LIFETIME_MS = 1800;  // How long bubbles live
  const THROTTLE_WINDOW_MS = 2000;    // Throttle window
  const MAX_REACTIONS_PER_WINDOW = 3; // Max reactions per user per window

  /**
   * Clean up all reactions and unsubscribe
   * Called by useLoungeSession when leaving
   */
  const cleanupReactions = useCallback(() => {
    console.log('[LoungeReactions] Cleaning up reactions');
    setActiveReactions([]);
    recentTimestampsRef.current = [];
    
    if (channelRef.current) {
      try {
        channelRef.current.unsubscribe();
        channelRef.current = null;
      } catch (err) {
        console.error('[LoungeReactions] Error unsubscribing:', err);
      }
    }
  }, []);

  /**
   * Subscribe to reactions channel
   * Listen for reactions from all users in the lounge
   */
  useEffect(() => {
    if (!loungeId || !ably) {
      console.warn('[LoungeReactions] Missing loungeId or ably client');
      return;
    }

    const channelName = `lounge:${loungeId}:reactions`;
    console.log(`[LoungeReactions] Subscribing to ${channelName}`);
    
    const channel = ably.channels.get(channelName);
    channelRef.current = channel;

    const handleReaction = (msg) => {
      const data = msg.data || msg;
      const { type, userId: fromUserId } = data;

      // Validate reaction type
      if (!REACTION_TYPES[type]) {
        console.warn('[LoungeReactions] Unknown reaction type:', type);
        return;
      }

      // Create new reaction bubble
      // 🔧 FIX: Constrain to center for more intentional burst pattern
      const id = uuidv4();
      const xPercent = 35 + Math.random() * 30; // Center zone (35-65%) for focused burst

      const reaction = {
        id,
        type,
        createdAt: Date.now(),
        xPercent,
      };

      console.log('[LoungeReactions] Received reaction:', type, 'from:', fromUserId);

      // Add to active reactions
      setActiveReactions((prev) => [...prev, reaction]);

      // Auto-remove after lifetime
      setTimeout(() => {
        setActiveReactions((prev) =>
          prev.filter((r) => r.id !== id)
        );
      }, REACTION_LIFETIME_MS);
    };

    channel.subscribe('reaction', handleReaction);

    // Cleanup on unmount
    return () => {
      try {
        channel.unsubscribe('reaction', handleReaction);
      } catch (err) {
        console.error('[LoungeReactions] Error during unmount cleanup:', err);
      }
    };
  }, [loungeId, ably]);

  /**
   * Register cleanup with session (Phase 1 integration)
   * Ensures reactions are cleared when leaving lounge
   */
  useEffect(() => {
    if (!registerCleanup) return;
    
    console.log('[LoungeReactions] Registering cleanup with session');
    const unregister = registerCleanup(cleanupReactions);
    
    return unregister;
  }, [registerCleanup, cleanupReactions]);

  /**
   * Send a reaction to the lounge
   * Throttled to prevent spam
   */
  const sendReaction = useCallback(
    async (type) => {
      if (!channelRef.current || !loungeId || !userId) {
        console.warn('[LoungeReactions] Cannot send reaction - missing data');
        return;
      }

      // Validate reaction type
      if (!REACTION_TYPES[type]) {
        console.warn('[LoungeReactions] Invalid reaction type:', type);
        return;
      }

      const now = Date.now();

      // Throttle check: max 3 reactions per 2 seconds
      const recent = recentTimestampsRef.current.filter(
        (t) => now - t < THROTTLE_WINDOW_MS
      );

      if (recent.length >= MAX_REACTIONS_PER_WINDOW) {
        console.warn('[LoungeReactions] Throttled - too many reactions');
        // Optionally show toast: "Take it easy! 😅"
        return;
      }

      // Add timestamp to recent list
      recent.push(now);
      recentTimestampsRef.current = recent;

      // Publish reaction to channel
      try {
        console.log('[LoungeReactions] Sending reaction:', type);
        await channelRef.current.publish('reaction', {
          type,
          userId,
          ts: now,
        });
      } catch (err) {
        console.error('[LoungeReactions] Failed to send reaction:', err);
      }
    },
    [loungeId, userId]
  );

  return {
    activeReactions,
    sendReaction,
    reactionTypes: REACTION_TYPES,
  };
}
